import { FC, useEffect, useState } from 'react';
import { useUserLanguage } from '@/customHooks';
import { capitalizeFirstLetter } from '@/helpers';
import { Property } from '@/custom-types';

type PropertyMultiSelectionListProps = {
  selected: (propertyIds: string[]) => void;
  properties: Property[] | undefined;
  initialSelectedIds?: string[];
};

const textsData = {
  en: {
    property: {
      name: 'Name',
      street: 'Street',
      city: 'City',
      state: 'State',
    },
  },
  es: {
    property: {
      name: 'Nombre',
      street: 'Calle',
      city: 'Ciudad',
      state: 'Estado',
    },
  },
};

export const PropertyMultiSelectionList: FC<PropertyMultiSelectionListProps> = ({
  selected,
  properties,
  initialSelectedIds = [],
}) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

  useEffect(() => {
    selected(selectedIds);
  }, [selectedIds, selected]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="py-6">
      <div className="grid h-[60vh] min-w-[300px] max-w-full w-full min-[500px]:min-w-[500px] grid-cols-[64px_1fr_1fr_1fr_1fr] content-start overflow-y-auto rounded-xl">
        <div className="contents">
          <div className="sticky top-0 z-10 border-b border-t border-gray-500 bg-white p-[15px] text-left font-bold" />
          <div className="sticky top-0 z-10 border-b border-t border-gray-500 bg-white p-[15px] text-left font-bold">
            {texts.property.name}
          </div>
          <div className="sticky top-0 z-10 border-b border-t border-gray-500 bg-white p-[15px] text-left font-bold">
            {texts.property.street}
          </div>
          <div className="sticky top-0 z-10 border-b border-t border-gray-500 bg-white p-[15px] text-left font-bold">
            {texts.property.city}
          </div>
          <div className="sticky top-0 z-10 border-b border-t border-gray-500 bg-white p-[15px] text-left font-bold">
            {texts.property.state}
          </div>
        </div>

        {properties?.map((property) => (
          <div key={property.id} className="contents group">
            <div className="flex min-h-[50px] items-center border-b border-gray-200 p-[15px] text-sm group-odd:bg-white group-even:bg-[--color-background-2]">
              <input
                type="checkbox"
                checked={selectedIds.includes(property.id)}
                onChange={() => handleToggle(property.id)}
                className="h-4 w-4 cursor-pointer accent-[#00183A]"
              />
            </div>
            <div className="flex min-h-[50px] items-center border-b border-gray-200 p-[15px] text-sm group-odd:bg-white group-even:bg-[--color-background-2]">
              {capitalizeFirstLetter(property.name)}
            </div>
            <div className="flex min-h-[50px] items-center border-b border-gray-200 p-[15px] text-sm group-odd:bg-white group-even:bg-[--color-background-2]">
              {capitalizeFirstLetter(property.address.street)}
            </div>
            <div className="flex min-h-[50px] items-center border-b border-gray-200 p-[15px] text-sm group-odd:bg-white group-even:bg-[--color-background-2]">
              {capitalizeFirstLetter(property.address.city?.name ?? '')}
            </div>
            <div className="flex min-h-[50px] items-center border-b border-gray-200 p-[15px] text-sm group-odd:bg-white group-even:bg-[--color-background-2]">
              {capitalizeFirstLetter(property.address.state?.name ?? '')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
